import {
  Building2,
  Check,
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { BrandMark, BrandName } from '../components/Brand';
import { useToast } from '../components/toast/ToastProvider';
import { Avatar } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Tip } from '../components/ui/tooltip';
import { useAuth } from '../features/auth/AuthContext';
import { ThemeToggle } from '../features/theme/ThemeToggle';
import { cn } from '../lib/utils';
import { CommandPalette, openCommandPalette } from './CommandPalette';
import { ErrorBoundary } from './ErrorBoundary';
import { GuidedTour } from './GuidedTour';
import { NAV_ITEMS, NAV_ITEMS_SYSTEM } from './nav';
import { RouteProgressBar } from './RouteProgressBar';
import { SidebarBrand, SidebarNav } from './Sidebar';
import { useSidebarCollapsed } from './useSidebarCollapsed';

/**
 * Shared between the sidebar-footer switcher and the topbar workspace pill,
 * so "which tenants can I reach, and how do I switch" is written once.
 */
function useWorkspaceSwitch() {
  const { session, switchTenant } = useAuth();
  const toast = useToast();
  const [switching, setSwitching] = useState(false);

  async function handleSwitch(tenantId: string) {
    if (!session?.tenant || tenantId === session.tenant.id || switching) return;
    setSwitching(true);
    try {
      await switchTenant(tenantId);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSwitching(false);
    }
  }

  return { session, switching, handleSwitch };
}

/** The tenant list shared by both switcher UIs below — same content,
 * different trigger. */
function WorkspaceMenuItems({
  session,
  handleSwitch,
}: {
  session: NonNullable<ReturnType<typeof useWorkspaceSwitch>['session']>;
  handleSwitch: (tenantId: string) => void;
}) {
  return (
    <>
      <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
      {session.tenants.map((tenant) => (
        <DropdownMenuItem key={tenant.id} onSelect={() => handleSwitch(tenant.id)}>
          <Avatar name={tenant.name} size="sm" />
          <span className="flex-1 truncate">{tenant.name}</span>
          {tenant.id === session.tenant?.id && <Check className="ml-auto size-3.5 shrink-0" />}
        </DropdownMenuItem>
      ))}
    </>
  );
}

/**
 * Anchored at the bottom of the sidebar. Static (with an avatar) for anyone
 * pinned to one tenant (BUSINESS_ADMIN/BUSINESS_MEMBER, or an OWNER/ADMIN
 * when the system only has one tenant so far); becomes a dropdown the
 * moment there's more than one tenant to switch between.
 */
function WorkspaceSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { session, switching, handleSwitch } = useWorkspaceSwitch();

  // No active tenant yet (a fresh install, or every workspace deactivated)
  // — nothing to show here until one exists; the rest of the shell (Team,
  // Workspaces, Settings) stays usable regardless.
  if (!session?.tenant) return null;

  if (session.tenants.length <= 1) {
    if (collapsed) {
      return (
        <div className="flex justify-center px-2 py-1.5">
          <Tip label={session.tenant.name} side="right">
            <Avatar name={session.tenant.name} size="sm" />
          </Tip>
        </div>
      );
    }
    return (
      <div className="px-3 py-1.5">
        <p className="label-micro px-3">Workspace</p>
        <div className="flex items-center gap-2 px-3 py-1">
          <Avatar name={session.tenant.name} size="sm" />
          <p className="truncate text-sm">{session.tenant.name}</p>
        </div>
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 py-1.5">
        <DropdownMenu>
          <Tip label={`Switch workspace (${session.tenant.name})`} side="right">
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={switching}
                aria-label={`Switch workspace (currently ${session.tenant.name})`}
                className="rounded-full transition-opacity hover:opacity-80 disabled:opacity-60"
              >
                <Avatar name={session.tenant.name} size="sm" />
              </button>
            </DropdownMenuTrigger>
          </Tip>
          <DropdownMenuContent align="start" side="right" className="w-56">
            <WorkspaceMenuItems session={session} handleSwitch={handleSwitch} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="px-3 py-1.5">
      <p className="label-micro px-3 pb-1.5">Workspace</p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={switching}
            className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-3 py-2 text-sm transition-colors hover:bg-muted disabled:opacity-60"
          >
            <Avatar name={session.tenant.name} size="sm" />
            <span className="flex-1 truncate text-left">{session.tenant.name}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <WorkspaceMenuItems session={session} handleSwitch={handleSwitch} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * The topbar's workspace pill — read-only, on purpose. Switching workspaces
 * lives only in the sidebar footer (`WorkspaceSwitcher`); a second
 * interactive entry point here was tried and explicitly not wanted — this
 * is just "which workspace's data am I looking at," not a control.
 */
function TopbarWorkspacePill() {
  const { session } = useAuth();
  if (!session?.tenant) return null;

  return (
    <span className="flex min-w-0 items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-1 text-xs text-muted-foreground">
      <Building2 className="size-3 shrink-0" />
      <span className="truncate">{session.tenant.name}</span>
    </span>
  );
}

/**
 * Anchored at the bottom of the sidebar, below the system nav section: which
 * workspace is active. Inviting an OWNER/ADMIN now lives on the Admins page
 * (see nav.ts's NAV_ITEMS_SYSTEM) rather than a shortcut here.
 *
 * Shrinks to just the tenant's avatar when the rail is collapsed — same
 * "icon only" treatment every other section gets — rather than disappearing
 * outright, so the bottom-anchored group (this + the collapse toggle) stays
 * in the same place on screen whether the sidebar is collapsed or not.
 */
function SidebarFooter({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className="border-t border-border py-2">
      <WorkspaceSwitcher collapsed={collapsed} />
    </div>
  );
}

function UserMenu() {
  const { session, logout } = useAuth();
  if (!session) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full border border-border transition-colors hover:border-border-strong"
          aria-label="Account menu"
        >
          <Avatar name={session.user.display_name || session.user.email} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel>Signed in</DropdownMenuLabel>
        <div className="px-2.5 pb-2">
          <p className="truncate text-sm font-medium">{session.user.email}</p>
          <p className="font-mono text-xs text-muted-foreground">{session.user.role}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The nav item (icon + label) whose route the current path is inside —
 * exact match first (so `/` doesn't prefix-match everything), then the
 * longest-prefix match for a nested route like `/chats/:chatId`. */
function useActiveNavItem() {
  const location = useLocation();
  const items = [...NAV_ITEMS, ...NAV_ITEMS_SYSTEM];
  const exact = items.find((item) => item.to === location.pathname);
  if (exact) return exact;
  const prefixMatches = items
    .filter((item) => item.to !== '/' && location.pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length);
  return prefixMatches[0];
}

export function AppShell() {
  const location = useLocation();
  const activeNavItem = useActiveNavItem();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const mainRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);

  // The scrolling container differs by breakpoint (<main> itself at lg+,
  // the whole page below it — see the layout comment on the outer <div>),
  // so this listens on the window in the capture phase: scroll doesn't
  // bubble, but it does fire during capture on every ancestor, which lets
  // one listener catch both cases (same technique GuidedTour.tsx uses to
  // track a moving target through the same split).
  useEffect(() => {
    function onScroll() {
      const main = mainRef.current;
      setScrolled((main != null && main.scrollTop > 4) || window.scrollY > 4);
    }
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  return (
    // Fixed at exactly the viewport height on lg+ (not min-h-screen, which
    // let a tall page — e.g. Settings — stretch this whole grid taller than
    // the screen and drag the sidebar along with it as the page scrolled).
    // Below lg there's no persistent sidebar to protect, so it stays plain
    // document flow. `overflow-hidden` on the grid pairs with `overflow-y-auto`
    // on <main> below: the sidebar and header never move, only the content does.
    <div
      className={cn(
        'min-h-screen lg:grid lg:h-screen lg:overflow-hidden',
        'transition-[grid-template-columns] duration-200',
        collapsed ? 'lg:grid-cols-[4.5rem_1fr]' : 'lg:grid-cols-[16rem_1fr]',
      )}
    >
      <RouteProgressBar />

      {/* Desktop sidebar */}
      <aside
        data-tour="sidebar-nav"
        className="surface-grid hidden border-r border-border bg-surface lg:flex lg:h-full lg:flex-col lg:overflow-y-auto"
      >
        <SidebarBrand collapsed={collapsed} />
        <div className="py-2">
          <SidebarNav items={NAV_ITEMS} collapsed={collapsed} label="Workspace" />
        </div>
        {/* The one flexible spacer: whatever height is left over collects
            here, so "Platform" + the workspace switcher + the collapse
            toggle sit together as one group anchored to the bottom of the
            rail — in both states, so that group never jumps position when
            the rail collapses. */}
        <div aria-hidden className="flex-1" />
        {/* System-level sections (Admins, Workspaces, Settings) — nothing
            here belongs to any one tenant, so they sit below a divider
            rather than mixed into the workspace-scoped list above. */}
        <div className="border-t border-border py-2">
          <SidebarNav items={NAV_ITEMS_SYSTEM} collapsed={collapsed} label="Platform" />
        </div>
        <SidebarFooter collapsed={collapsed} />
        <div className="border-t border-border p-2">
          <Tip label={collapsed ? 'Expand sidebar' : undefined} side="right">
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={cn(
                'flex w-full items-center rounded-[var(--radius-control)] py-2 text-sm',
                'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                collapsed ? 'justify-center px-2' : 'gap-3 px-3',
              )}
            >
              {collapsed ? (
                <PanelLeftOpen className="size-4 shrink-0" />
              ) : (
                <>
                  <PanelLeftClose className="size-4 shrink-0" />
                  <span>Collapse</span>
                </>
              )}
            </button>
          </Tip>
        </div>
      </aside>

      <div className="relative flex min-h-screen flex-col lg:h-full lg:min-h-0">
        {/* A soft accent wash behind the header/top-of-page — the same
            language as the login screen's ambience, toned all the way down
            for all-day use. Sits behind everything via DOM order alone, no
            z-index needed; the header's own blur picks up a hint of it. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,color-mix(in_oklab,var(--primary)_7%,transparent),transparent_70%)]"
        />

        <header
          className={cn(
            'sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b px-4 backdrop-blur-md lg:px-8',
            'transition-[box-shadow,background-color,border-color] duration-200',
            scrolled
              ? 'border-border-strong bg-background/95 shadow-md shadow-black/5'
              : 'border-border bg-background/80',
          )}
        >
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Open navigation"
              >
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SidebarBrand />
              <div className="flex-1 overflow-y-auto py-2">
                <SidebarNav
                  items={NAV_ITEMS}
                  onNavigate={() => setMobileOpen(false)}
                  label="Workspace"
                />
              </div>
              <div className="border-t border-border py-2">
                <SidebarNav
                  items={NAV_ITEMS_SYSTEM}
                  onNavigate={() => setMobileOpen(false)}
                  label="Platform"
                />
              </div>
              <SidebarFooter />
            </SheetContent>
          </Sheet>

          <span className="flex items-center gap-2 lg:hidden">
            <BrandMark className="size-7" />
            <BrandName className="text-base" />
          </span>

          {/* Which workspace's data every tenant-scoped page (Overview,
              Assistants, Knowledge, Conversations, Usage, API keys) is
              currently showing — visible on every screen size, since the
              sidebar can be collapsed on mobile. Doubles as a second
              workspace-switch entry point when there's more than one. */}
          <TopbarWorkspacePill />

          {/* Which page, specifically — the sidebar's active-item highlight
              already answers this at lg+, but there's no sidebar at all
              below it, and even at lg+ it's a handy "you are here" once
              you've scrolled past the page's own header card. */}
          {activeNavItem && (
            <div className="hidden min-w-0 items-center gap-1.5 text-sm text-muted-foreground sm:flex">
              <ChevronRight aria-hidden className="size-3.5 shrink-0" />
              <activeNavItem.icon aria-hidden className="size-3.5 shrink-0" />
              <span className="truncate font-medium text-foreground">{activeNavItem.label}</span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            <Button
              variant="ghost"
              onClick={openCommandPalette}
              data-tour="search"
              className="hidden gap-2 text-muted-foreground sm:inline-flex"
            >
              <Search className="size-4" aria-hidden />
              <span>Search</span>
              <kbd className="ml-1 rounded border border-border-strong px-1 font-mono text-[0.6875rem] leading-4">
                ⌘K
              </kbd>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={openCommandPalette}
              aria-label="Search"
              tooltip="Search (⌘K)"
              className="sm:hidden"
            >
              <Search />
            </Button>
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <main
          ref={mainRef}
          className="surface-grid relative flex-1 px-4 py-8 lg:overflow-y-auto lg:px-8"
        >
          <div className="relative mx-auto w-full max-w-6xl">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <CommandPalette />
      <GuidedTour />
    </div>
  );
}
